if (!customElements.get("td-pdp-side-drawer")) {
  class TdPdpSideDrawer extends HTMLElement {
    constructor() {
      super();
      this.titleButtons = this.querySelectorAll("[data-side-drawer]");
      this.body = document.querySelector("body");
      this.overlayClass = "td-sd__overlay";
      this.openerElement = null;
      this.keydownHandler = (event) => this.handleKeydown(event);
    }

    connectedCallback() {
      this.initSideDrawer();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.keydownHandler);
    }

    initSideDrawer() {
      this.titleButtons.forEach((title) => {
        title.addEventListener("click", (event) => {
          const drawerNumber = title.dataset.sideDrawer;
          const contentWrapper = this.querySelector(
            `[data-side-drawer-${drawerNumber}-wrapper]`
          );

          this.querySelectorAll(".td-sd__content-wrapper--active").forEach((openWrapper) => {
            if (openWrapper !== contentWrapper) {
              openWrapper.classList.remove("td-sd__content-wrapper--active");
              openWrapper.removeAttribute("aria-modal");
              this.syncTriggers(openWrapper, false);
            }
          });

          if (contentWrapper) {
            const isActive = contentWrapper.classList.contains("td-sd__content-wrapper--active");

            if (isActive) {
              this.closeDrawer(contentWrapper);
            } else {
              this.openDrawer(contentWrapper);
            }
          }
        });
      });

      const closeButtons = this.querySelectorAll(".td-sd__close");
      closeButtons.forEach((closeButton) => {
        closeButton.addEventListener("click", (event) => {
          const contentWrapper = closeButton.closest(".td-sd__content-wrapper");
          if (contentWrapper) {
            this.closeDrawer(contentWrapper);
          }
          event.stopPropagation();
        });
      });

      this.body.addEventListener("click", (event) => {
        if (event.target === this.body && this.body.classList.contains(this.overlayClass)) {
          const contentWrapper = this.querySelector(".td-sd__content-wrapper--active");
          
          if (contentWrapper) {
            this.closeDrawer(contentWrapper);
          }
        }
      });

      // External triggers: use delegation so triggers added later (e.g. bold links from product selector) work
      const sectionId = this.getAttribute("data-section-id");
      const instanceId = this.getAttribute("data-drawer-instance");
      if (!sectionId && !instanceId) return;

      const bindDelegatedTrigger = () => {
        if (document.__tdSideDrawerTriggerDelegation) return;
        document.__tdSideDrawerTriggerDelegation = true;
        document.addEventListener("click", (e) => {
          const trigger = e.target.closest("[data-trigger-side-drawer]");
          if (!trigger) return;
          const triggerInstance = trigger.getAttribute("data-drawer-instance");
          const triggerSection = trigger.getAttribute("data-drawer-section-id");
          const drawer = triggerInstance
            ? document.querySelector(`td-pdp-side-drawer[data-drawer-instance="${triggerInstance}"]`)
            : document.querySelector(`td-pdp-side-drawer[data-section-id="${triggerSection}"]`);
          if (!drawer) return;
          const drawerNumber = trigger.getAttribute("data-trigger-side-drawer");
          const contentWrapper = drawer.querySelector(
            `[data-side-drawer-${drawerNumber}-wrapper]`
          );
          if (contentWrapper) {
            e.preventDefault();
            drawer.openDrawer(contentWrapper);
          }
        });
      };
      bindDelegatedTrigger();
      
    }

    openDrawer(contentWrapper) {
      if (this.body) {
        this.body.classList.add(this.overlayClass);
        this.lockBodyScroll();
      }

      contentWrapper.classList.add("td-sd__content-wrapper--active");

      // The drawer sits over the page behind it, so it has to hold focus while it is open and
      // hand it back to whatever opened it on close.
      this.openerElement = document.activeElement;
      contentWrapper.setAttribute("aria-modal", "true");
      this.syncTriggers(contentWrapper, true);
      contentWrapper.querySelector("button.td-sd__close")?.focus({ preventScroll: true });
      document.addEventListener("keydown", this.keydownHandler);
    }

    closeDrawer(contentWrapper) {
      contentWrapper.classList.remove("td-sd__content-wrapper--active");
      contentWrapper.removeAttribute("aria-modal");
      this.syncTriggers(contentWrapper, false);

      if (!this.querySelector(".td-sd__content-wrapper--active")) {
        document.removeEventListener("keydown", this.keydownHandler);
        this.restoreFocus();

        if (this.body) {
          this.body.classList.remove(this.overlayClass);
          this.unlockBodyScroll();
        }
      }
    }

    closeAllDrawers() {
      const hadActiveDrawer = Boolean(this.querySelector(".td-sd__content-wrapper--active"));

      this.querySelectorAll(".td-sd__content-wrapper--active").forEach((openWrapper) => {
        openWrapper.classList.remove("td-sd__content-wrapper--active");
        openWrapper.removeAttribute("aria-modal");
        this.syncTriggers(openWrapper, false);
      });

      document.removeEventListener("keydown", this.keydownHandler);
      if (hadActiveDrawer) {
        this.restoreFocus();
      }

      if (this.body) {
        this.body.classList.remove(this.overlayClass);
        if (hadActiveDrawer) {
          this.unlockBodyScroll();
        }
      }
    }

    // Keeps every trigger pointed at this panel (aria-controls) in step with the panel's state.
    syncTriggers(contentWrapper, isOpen) {
      if (!contentWrapper.id) return;

      document
        .querySelectorAll(`[aria-controls="${contentWrapper.id}"]`)
        .forEach((trigger) => trigger.setAttribute("aria-expanded", isOpen ? "true" : "false"));
    }

    restoreFocus() {
      const opener = this.openerElement;
      this.openerElement = null;

      if (opener && opener.isConnected && typeof opener.focus === "function") {
        opener.focus({ preventScroll: true });
      }
    }

    handleKeydown(event) {
      const contentWrapper = this.querySelector(".td-sd__content-wrapper--active");
      if (!contentWrapper) return;

      if (event.key === "Escape") {
        this.closeDrawer(contentWrapper);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = contentWrapper.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!contentWrapper.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    lockBodyScroll() {
      if (!this.body) return;
      if (!window.TdBodyScrollLock || typeof window.TdBodyScrollLock.lock !== "function") return;
      window.TdBodyScrollLock.lock(this.body);
    }

    unlockBodyScroll() {
      if (!this.body) return;
      if (!window.TdBodyScrollLock || typeof window.TdBodyScrollLock.unlock !== "function") return;
      window.TdBodyScrollLock.unlock(this.body);
    }
  }

  customElements.define("td-pdp-side-drawer", TdPdpSideDrawer);
}
